/**
 * PATH: packages/ui/src/components/global-search/matching.ts
 * FILE_ID: UI-GLOBALSEARCH-MATCHING-001
 * PURPOSE: THE canonical client-side text-match contract for search
 *          surfaces: AND-of-tokens, each token a case-insensitive
 *          substring of ANY searched field.
 * WHY: Search-architecture plan W4 (2026-07-07). The same matching bug
 *      shipped twice because each surface hand-rolled its own predicate:
 *      BUG-2026-07-06-KANBAN-ONBOARD-SEARCH-WHOLE-STRING (currentBoard
 *      matched the whole query as one substring, missing "internal doors"
 *      → "Internal and Entry Doors") after the identical gap was fixed in
 *      the navigation provider the same week. The backend already speaks
 *      AND-per-token (`_card_token_q`, `apply_v2_search`); every NEW
 *      client-side matcher must import this module instead of re-rolling.
 * FLOW: (query) → tokens; (fields, tokens) → boolean (every token found
 *       in at least one field)
 * DEPENDENCIES: none (pure I→O).
 * RELATED:
 *  - useSearch.ts searchLocalJobs (first adopter — was the last remaining
 *    whole-string matcher), providers/currentBoard.ts + navigation.ts
 *    (same semantics, candidate migrations), highlight.tsx tokenizeQuery
 *    (display-side tokenizer — different concerns: it splits on ':' and
 *    sorts longest-first for mark placement; do not merge the two).
 */

/** Split a query into lowercase AND-tokens. Whitespace-separated;
 *  empty/blank queries yield []. Pure I→O. */
export function tokenizeSearchQuery(query: string | null | undefined): string[] {
  return (query ?? '')
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
}

/** True when EVERY token appears as a substring of AT LEAST ONE field
 *  (case-insensitive). Empty token list matches nothing — callers gate on
 *  a min query length, and "no query" must never mean "everything" at a
 *  matcher seam. Nullish fields are skipped. Pure I→O. */
export function allTokensMatchFields(
  fields: ReadonlyArray<string | null | undefined>,
  tokens: ReadonlyArray<string>,
): boolean {
  if (tokens.length === 0) return false
  const haystacks = fields
    .map((f) => (f == null ? '' : String(f).toLowerCase()))
    .filter(Boolean)
  if (haystacks.length === 0) return false
  return tokens.every((tok) => haystacks.some((h) => h.includes(tok)))
}
